import { create } from "zustand";
import { IStore } from "@/types/interfaces";
import { Preset, RGBA } from "@/types/Color";
import { logger } from "@/utils/logger";
import { PaintMode } from "@/types/Color";
import { FDMSlot } from "@/types/Color";
import { ColorEngine } from "@/services/color-engine/ColorEngine";

interface ColorStoreState {
  presets: Preset[];
  paintMode: PaintMode;
  currentColor: string;
  brushSize: number;
  opacity: number;
  isPainting: boolean;
  absLock: boolean;
  relLock: boolean;
  pickedMesh: any;
  paintModeOptions: Record<string, Record<string, boolean>>;
  colorData: Blob | null;
  fdmMode: boolean;
  fdmSlots: FDMSlot[];
  fdmCurrentSlotIndex: number;
}

interface ColorStoreActions extends IStore {
  // Actions
  setPaintMode: (mode: PaintMode) => void;
  setCurrentColor: (color: string) => void;
  setBrushSize: (size: number) => void;
  setOpacity: (opacity: number) => void;
  setIsPainting: (painting: boolean) => void;
  setAbsLock: (lock: boolean) => void;
  setRelLock: (lock: boolean) => void;
  setPickedMesh: (mesh: any) => void;
  setPaintModeOption: (modeKey: string, option: string) => void;
  setColorData: (data: Blob | null) => void;
  fetchPresets: () => Promise<void>;
  // State getters
  getColorState: () => ColorStoreState;
  resetFdm: () => void;
  setFdmMode: (value: boolean, shareOrSave?: boolean) => void;
  setFdmCurrentSlotIndex: (index: number) => void;
  setFdmSlotsColorByIndex: (index: number, color: RGBA) => void;
  setFdmSlots: (slots: FDMSlot[]) => void;
  setFdmSlotsMeshesByIndex: (index: number, mesh: string) => void;
  resizeFdmSlots: (n: number) => void;
}

const initialState: ColorStoreState = {
  presets: [],
  paintMode: PaintMode.Fill,
  currentColor: "#ff0000",
  brushSize: 5,
  opacity: 1.0,
  isPainting: false,
  absLock: false,
  relLock: false,
  pickedMesh: null,
  paintModeOptions: {
    Fill: { part: true, object: false },
    Brush: { wash: false, drybrush: false, paint: true }
  },
  colorData: null,
  fdmMode: false,
  fdmSlots: [
    { color: { r: 0, g: 0, b: 0, a: 0 }, meshes: new Set([]) },
    { color: { r: 0, g: 0, b: 0, a: 0 }, meshes: new Set([]) },
    { color: { r: 0, g: 0, b: 0, a: 0 }, meshes: new Set([]) },
    { color: { r: 0, g: 0, b: 0, a: 0 }, meshes: new Set([]) }
  ],
  fdmCurrentSlotIndex: 0
};

export const useColorStore = create<ColorStoreState & ColorStoreActions>(
  (set: any, get: any) => ({
    ...initialState,

    initialize: async (): Promise<boolean> => {
      try {
        // Initialize color store
        return true;
      } catch (error) {
        return false;
      }
    },

    reset: () => {
      set(initialState);
    },

    getStatus: () => {
      return {
        isInitialized: true
      };
    },

    // Actions
    setPaintMode: (mode: PaintMode) => {
      set((state: ColorStoreState) => ({
        ...state,
        paintMode: mode
      }));
    },

    setCurrentColor: (color: string) => {
      set((state: ColorStoreState) => ({
        ...state,
        currentColor: color
      }));
    },

    setBrushSize: (size: number) => {
      set((state: ColorStoreState) => ({
        ...state,
        brushSize: Math.max(1, Math.min(100, size))
      }));
    },

    setOpacity: (opacity: number) => {
      set((state: ColorStoreState) => ({
        ...state,
        opacity: Math.max(0, Math.min(1, opacity))
      }));
    },

    setIsPainting: (painting: boolean) => {
      set((state: ColorStoreState) => ({
        ...state,
        isPainting: painting
      }));
    },

    setAbsLock: (lock: boolean) => {
      set((state: ColorStoreState) => ({
        ...state,
        absLock: lock
      }));
    },

    setRelLock: (lock: boolean) => {
      set((state: ColorStoreState) => ({
        ...state,
        relLock: lock
      }));
    },

    setPickedMesh: (mesh: any) => {
      set((state: ColorStoreState) => ({
        ...state,
        pickedMesh: mesh
      }));
    },

    setPaintModeOption: (modeKey: string, option: string) => {
      set((state: ColorStoreState) => {
        const modeOptions = state.paintModeOptions[modeKey];
        if (!modeOptions) return state;

        // Create updated options with selected option set to true and others to false
        const updatedOptions = Object.keys(modeOptions).reduce(
          (acc, key) => {
            acc[key] = key === option;
            return acc;
          },
          {} as Record<string, boolean>
        );

        return {
          ...state,
          paintModeOptions: {
            ...state.paintModeOptions,
            [modeKey]: updatedOptions
          }
        };
      });
    },

    setColorData: (data: Blob | null) => {
      set((state: ColorStoreState) => ({
        ...state,
        colorData: data
      }));
    },

    fetchPresets: async (): Promise<void> => {
      try {
        const url = `${import.meta.env.VITE_DOMAIN}staticData/presets.json`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        const presets: Preset[] = [];
        for (const preset of data) {
          presets.push({
            img: preset.path,
            brushProp: preset.colorTriplet.brushProp,
            renderingProp: preset.colorTriplet.renderingProp,
            ...preset
          });
        }
        set((state: ColorStoreState) => ({
          ...state,
          presets
        }));
      } catch (error) {
        logger.error("Failed to fetch presets data", error, "ColorStore");
      }
    },
    // State getters
    getColorState: () => {
      const state = get();
      return {
        presets: state.presets,
        currentColor: state.currentColor,
        paintMode: state.paintMode,
        brushSize: state.brushSize,
        opacity: state.opacity,
        isPainting: state.isPainting,
        absLock: state.absLock,
        relLock: state.relLock,
        pickedMesh: state.pickedMesh,
        paintModeOptions: state.paintModeOptions,
        colorData: state.colorData,
        fdmMode: state.fdmMode,
        fdmSlots: state.fdmSlots,
        fdmCurrentSlotIndex: state.fdmCurrentSlotIndex
      };
    },
    resetFdm: () => {
      set((state: ColorStoreState) => ({
        ...state,
        fdmCurrentSlotIndex: 0,
        fdmSlots: [
          { color: { r: 0, g: 0, b: 0, a: 0 }, meshes: new Set([]) },
          { color: { r: 0, g: 0, b: 0, a: 0 }, meshes: new Set([]) },
          { color: { r: 0, g: 0, b: 0, a: 0 }, meshes: new Set([]) },
          { color: { r: 0, g: 0, b: 0, a: 0 }, meshes: new Set([]) }
        ]
      }));
    },

    setFdmMode: (value: boolean, shareOrSave: boolean = false) => {
      const colorEngine = ColorEngine.getInstance();
      if (value) {
        set((state: ColorStoreState) => ({
          ...state,
          paintMode: PaintMode.Fill,
          absLock: false,
          relLock: false,
          pickedMesh: null,
          fdmMode: value
        }));
        colorEngine.resetProps();
        if (!shareOrSave) {
          colorEngine.enableFdmMaterials();
          colorEngine.initFdmSlots();
        }
      }

      if (!value) {
        set((state: ColorStoreState) => ({
          ...state,
          fdmMode: value
        }));
        colorEngine.disableFdmMaterials();
        get().resetFdm();
        // Restore color pipeline when leaving FDM mode
        //Ef3dContext.renderManager.setRenderingPipeline(Ef3dContext.color);
      }
    },
    setFdmCurrentSlotIndex: (index: number) => {
      set((state: ColorStoreState) => ({
        ...state,
        fdmCurrentSlotIndex: index
      }));
    },

    setFdmSlotsColorByIndex: (index: number, color: RGBA) => {
      const state = get();
      const newSlots = [...state.fdmSlots];
      newSlots[index] = { ...newSlots[index], color };
      set((state: ColorStoreState) => ({
        ...state,
        fdmSlots: newSlots
      }));
    },
    setFdmSlots: (slots: FDMSlot[]) => {
      set((state: ColorStoreState) => ({
        ...state,
        fdmSlots: slots.map((slot) => ({
          color: slot.color,
          meshes: new Set(slot.meshes)
        }))
      }));
    },

    setFdmSlotsMeshesByIndex: (index: number, mesh: string) => {
      const state = get();
      const newSlots = [...state.fdmSlots];
      for (let i = 0; i < state.fdmSlots.length; i++) {
        if (i === index) {
          if (!newSlots[index].meshes.has(mesh)) {
            newSlots[index].meshes.add(mesh);
          }
          continue;
        }
        if (newSlots[i].meshes.has(mesh)) {
          newSlots[i].meshes.delete(mesh);
        }
      }
      set((state: ColorStoreState) => ({
        ...state,
        fdmSlots: newSlots
      }));
    },

    resizeFdmSlots: (n: number) => {
      const state = get();
      const current = state.fdmSlots.length;

      if (current < n) {
        // Add missing slots
        const toAdd = n - current;
        const newSlots = [...state.fdmSlots];
        for (let i = 0; i < toAdd; i++) {
          newSlots.push({
            color: { r: 0, g: 0, b: 0, a: 1 },
            meshes: new Set()
          });
        }
        set((state: ColorStoreState) => ({
          ...state,
          fdmSlots: newSlots
        }));
      } else if (current > n) {
        const meshes = [];
        const index = state.fdmSlots.length - (current - n);
        for (let i = index; i < state.fdmSlots.length; i++) {
          meshes.push(...state.fdmSlots[i].meshes);
        }
        // Remove extra slots from the end
        const newSlots = state.fdmSlots.slice(0, n);
        newSlots[0].meshes = new Set([...newSlots[0].meshes, ...meshes]);
        const colorEngine = ColorEngine.getInstance();
        colorEngine.handleFdmCountOfSlotsChange(0);
        set((state: ColorStoreState) => ({
          ...state,
          fdmSlots: newSlots,
          fdmCurrentSlotIndex: newSlots.length - 1
        }));
      }
    }
  })
);
